'use strict';

const { subtle } = require('./lib');

// Per-user did:web identity. The DID string uses the product domain, but our verify
// page resolves by HANDLE against the serving origin (works in dev and prod), so the
// DID is a stable identifier independent of where it's hosted.
const DID_DOMAIN = 'web3keys.com';

async function sha256Hex(str) {
  const buf = await subtle().digest('SHA-256', new TextEncoder().encode(str));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Deterministic, collision-resistant handle from the identity public key.
async function deriveHandle(identityPubkeyHex) {
  return (await sha256Hex(identityPubkeyHex)).slice(0, 20);
}

function buildDid(handle, domain = DID_DOMAIN) {
  return `did:web:${domain}:u:${handle}`;
}

// Extract the handle from a did:web:...:u:<handle> string.
function handleFromDid(did) {
  const m = String(did).split(':u:');
  return m.length > 1 ? m[1] : null;
}

// The signing key is the mnemonic-derived secp256k1 identity key, so VCs are ES256K
// and the JWK is a secp256k1 EC key.
function buildDidDocument(did, publicJwk, kid) {
  const vmId = `${did}#${kid}`;
  return {
    '@context': ['https://www.w3.org/ns/did/v1', 'https://w3id.org/security/suites/jws-2020/v1'],
    id: did,
    verificationMethod: [{
      id: vmId,
      type: 'JsonWebKey2020',
      controller: did,
      publicKeyJwk: Object.assign({}, publicJwk, { kid, alg: 'ES256K', use: 'sig' }),
    }],
    assertionMethod: [vmId],
    authentication: [vmId],
  };
}

function buildJwks(publicJwk, kid) {
  return { keys: [Object.assign({}, publicJwk, { kid, alg: 'ES256K', use: 'sig' })] };
}

module.exports = { DID_DOMAIN, sha256Hex, deriveHandle, buildDid, handleFromDid, buildDidDocument, buildJwks };
